import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { bookingHref } from '../../config';

const BookingContext = createContext({ open: () => {} });

export const useBooking = () => useContext(BookingContext);

export const BookingProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close]);

  return (
    <BookingContext.Provider value={{ open }}>
      {children}
      {isOpen && <Modal onClose={close} />}
    </BookingContext.Provider>
  );
};

const Modal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
    role="dialog"
    aria-modal="true"
    aria-labelledby="booking-title"
  >
    <div
      className="absolute inset-0 bg-secondary-900/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    />

    <div className="relative w-full max-w-lg bg-cream border border-secondary-200 rounded-lg shadow-2xl animate-fade-in">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 p-1.5 text-secondary-400 hover:text-secondary-800 transition-colors"
      >
        <XMarkIcon className="w-5 h-5" />
      </button>

      <div className="px-8 sm:px-12 py-12 sm:py-14">
        <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em] mb-5">
          Request a conversation
        </p>
        <h2
          id="booking-title"
          className="font-serif text-3xl sm:text-4xl font-normal text-secondary-900 tracking-tight mb-5"
        >
          Let&rsquo;s find a time.
        </h2>
        <p className="text-secondary-600 leading-relaxed mb-9 max-w-sm">
          You&rsquo;ll choose a slot on Amy&rsquo;s calendar. A private Google
          Meet is created for you automatically — around thirty minutes, and
          entirely confidential.
        </p>

        <a
          href={bookingHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="inline-flex items-center justify-center rounded bg-primary-800 text-cream font-medium tracking-wide px-8 py-4 hover:bg-primary-700 transition-colors duration-300"
        >
          Open the calendar
        </a>
        <p className="mt-4 text-xs text-secondary-400">
          Opens in a new tab.
        </p>
      </div>
    </div>
  </div>
);

export default BookingProvider;
